import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Layout } from "../../components/layout/Layout";
import Navbar from "../../components/navbar/Navbar";
import { getAwards } from "../../data/awards";
import { Pagination, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { BsArrowRightCircle, BsArrowLeftCircle } from "react-icons/bs";
import Footer from "../../components/footer/Footer";
import CookieBanner from "../../components/cookie/CookieBanner";
import { AwardsPage } from "../../components/helmet/ReactHelmet";
import AOS from "aos";
import "aos/dist/aos.css";

function Awards() {
  const headingRef = useRef(null);
  const descRef = useRef(null);
  const titleRefs = useRef([]); // Use a ref array to target each title
  const yearRefs = useRef([]); // Use a ref array for the years
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);

  // Initialize refs arrays
  titleRefs.current = [];
  yearRefs.current = [];

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 1000,
    });
  }, []);

  // Fetch awards data
  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const awardsData = await getAwards();
        setAwards(awardsData);
      } catch (error) {
        console.error("Error fetching awards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, []);

  const addToRefs = (el, refArray) => {
    if (el && !refArray.current.includes(el)) {
      refArray.current.push(el);
    }
  };

  useEffect(() => {
    if (loading || awards.length === 0) return;

    gsap.registerPlugin(ScrollTrigger);

    // Create award cards with staggered entrance
    awards.forEach((award, index) => {
      const yearRef = yearRefs.current[index];
      const titleRef = titleRefs.current[index];

      if (yearRef && titleRef) {
        // Create a card container effect
        // const awardCard = yearRef.closest('.award-card');

        // Year with simple fade up animation
        gsap.fromTo(
          yearRef,
          {
            opacity: 0,
            y: 50,
            scale: 0.95,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: "power2.out",
            scrollTrigger: {
              trigger: yearRef,
              start: "top 85%",
              end: "top 25%",
              toggleActions: "play none none reverse",
              scrub: false,
            },
          }
        );

        // Add subtle floating animation to year
        gsap.to(yearRef, {
          y: "+=3",
          duration: 3 + index * 0.5,
          ease: "power1.inOut",
          yoyo: true,
          repeat: -1,
          delay: 2 + index * 0.2,
        });

        // Title with simple fade up animation
        gsap.fromTo(
          titleRef,
          {
            opacity: 0,
            y: 30,
            scale: 0.95,
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1,
            ease: "power2.out",
            delay: 0.2,
            scrollTrigger: {
              trigger: titleRef,
              start: "top 85%",
              end: "top 25%",
              toggleActions: "play none none reverse",
              scrub: false,
            },
          }
        );
      }
    });

    // Simple and smooth image animations
    document.querySelectorAll(".swiper-slide img").forEach((img, index) => {
      gsap.fromTo(
        img,
        {
          opacity: 0,
          y: 20,
          scale: 0.95,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: img,
            start: "top 85%",
            end: "top 20%",
            toggleActions: "play none none reverse",
            scrub: false,
          },
        }
      );
    });

    // Cleanup on unmount
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [loading, awards]);

  if (loading) {
    return (
      <div className="bg-bgWhite min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading awards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bgWhite">
      <AwardsPage />
      <Navbar />
      <CookieBanner />
      <div className="pt-4 sm:pt-6 md:pt-16 lg:pt-12 xl:pt-16 bg-bgWhite">
        <Layout>
          <h1
            data-aos="fade-up"
            data-aos-delay="300"
            className="font-arimo text-3xl sm:text-4xl md:text-5xl lg:text-6xl 2xl:text-7xl font-semibold"
          >
            Awards & Recognitions
          </h1>
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-3/6"></div>
            <p
              data-aos="fade-up"
              data-aos-delay="600"
              className="mt-3 md:mt-5 xl:mt-12 lg:w-2/6 self-end"
            >
              Step into the world of ELF Shipping's Awards and Recognition.
              Here, we take great pride in highlighting our exceptional journey
              in logistics and exhibition logistics. Join us in celebrating the
              milestones, innovations, and the trust our valued partners have
              bestowed upon us.
            </p>
          </div>

          {awards.map((item, index) => (
            <div
              className="award-card mt-12 sm:mt-16 md:mt-20 lg:mt-24 relative"
              key={index}
            >
              {/* Award Card Container */}
              <div className="relative bg-white rounded-2xl p-6 md:p-8 lg:p-10 shadow-lg border border-gray-200">
                {/* Year Badge */}
                <div className="mb-6">
                  <span
                    ref={(el) => addToRefs(el, yearRefs)}
                    className="text-5xl md:text-6xl lg:text-7xl text-graydark font-semibold"
                    style={{
                      willChange: "transform, opacity",
                    }}
                  >
                    {item.year}
                  </span>
                </div>

                {/* Title */}
                <h3
                  ref={(el) => addToRefs(el, titleRefs)}
                  className="text-2xl md:text-3xl lg:text-4xl 2xl:text-5xl font-semibold mb-6 text-navyBlue"
                  style={{
                    willChange: "transform, opacity",
                  }}
                >
                  {item.title}
                </h3>

                {/* Image Gallery */}
                {item.images && (
                  <div className="relative">
                    <Swiper
                      slidesPerView={1}
                      spaceBetween={30}
                      breakpoints={{
                        0: {
                          slidesPerView: 1.4,
                          spaceBetween: 20,
                        },
                        768: {
                          slidesPerView: 2,
                          spaceBetween: 25,
                        },
                        1260: {
                          slidesPerView: 2,
                          spaceBetween: 30,
                        },
                      }}
                      pagination={{ clickable: true }}
                      navigation={{
                        nextEl: ".custom-swiper-button-next",
                        prevEl: ".custom-swiper-button-prev",
                      }}
                      modules={[Pagination, Navigation]}
                      className="mySwiper"
                      onSwiper={(swiper) => {
                        swiper.navigation.init();
                        swiper.navigation.update();
                      }}
                    >
                      {item.images.map((image, idx) => (
                        <SwiperSlide
                          key={idx}
                          className="rounded-xl lg:rounded-[32px] my-4 md:my-6 lg:my-8 bg-transparent"
                        >
                          <img
                            src={image.img}
                            alt={image.alt}
                            className="bg-transparent rounded-xl lg:rounded-[32px] lg:max-h-[70vh] drop-shadow-xl w-full"
                            style={{
                              willChange: "transform, opacity",
                            }}
                          />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="absolute top-[-50px] right-0 z-10 flex gap-2 mr-4 md:top-[-50px]">
                      <div className="custom-swiper-button-prev hidden md:block text-lineblue cursor-pointer">
                        <BsArrowLeftCircle size={30} />
                      </div>
                      <div className="custom-swiper-button-next hidden md:block text-lineblue cursor-pointer">
                        <BsArrowRightCircle size={30} />
                      </div>
                    </div>
                  </div>
                )}

                <p className="lg:w-[80vw] xl:w-[75vw] 2xl:w-[70vw]">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </Layout>
        <style>{`
          .swiper-pagination {
            display: none;
          }
        `}</style>
      </div>
      <div className="mt-12 md:mt-16 lg:mt-24"></div>
      <Footer />
    </div>
  );
}

export default Awards;
